import type { PublicScheduleCategory, PublicSchedulesResponse } from "../Api/schedules";
import { convertToTimezone, convertToTimezoneLocalized } from "../Common/dateTimeUtils";

// TODO: Include per-bracket schedule info in the public brackets listing response (reusing the backend
// \Panel\PublicBracket\Schedule\PublicBracketScheduleProvider) so the listing page no longer needs a second
// request to /api/events/{id}/schedules, then remove the client-side merge logic in this file.
export type PublicBracketSegmentInfo = {
    matNames: string[];
    etaStart: string | null;
    etaEnd: string | null;
    roundMarkerFrom: string | null;
    roundMarkerTo: string | null;
};

export type PublicBracketScheduleInfo = {
    // One entry per distinct round-coverage segment. Equal-parts slots sharing the
    // same round markers are merged (different mats, same time block). Segments are
    // sorted chronologically by etaStart so the first entry is always the earliest.
    segments: PublicBracketSegmentInfo[];
    // Union of matNames across all segments — used for search and the mat cell.
    allMatNames: string[];
};

type IntermediateSegment = PublicBracketSegmentInfo & { parameterId: number };

export function buildPublicBracketScheduleInfoMap(response: PublicSchedulesResponse): Map<number, PublicBracketScheduleInfo> {
    return groupByParameterId(collectSegments(response));
}

function collectSegments(response: PublicSchedulesResponse): Map<string, IntermediateSegment> {
    const segmentMap = new Map<string, IntermediateSegment>();
    for (const schedule of response.schedules) {
        for (const mat of schedule.mats) {
            for (const category of mat.categories) {
                mergeCategory(segmentMap, mat.name, category);
            }
        }
    }
    return segmentMap;
}

function mergeCategory(segmentMap: Map<string, IntermediateSegment>, matName: string, category: PublicScheduleCategory): void {
    const key = `${category.id}_${category.roundMarkerFrom ?? ""}_${category.roundMarkerTo ?? ""}`;
    const time = category.scheduledCategoryTime;
    const existing = segmentMap.get(key);
    if (!existing) {
        segmentMap.set(key, {
            parameterId: category.id,
            matNames: [matName],
            etaStart: time.start,
            etaEnd: time.end,
            roundMarkerFrom: category.roundMarkerFrom,
            roundMarkerTo: category.roundMarkerTo,
        });
        return;
    }
    if (!existing.matNames.includes(matName)) {
        existing.matNames.push(matName);
    }
    if (existing.etaStart === null || time.start < existing.etaStart) {
        existing.etaStart = time.start;
    }
    if (existing.etaEnd === null || time.end > existing.etaEnd) {
        existing.etaEnd = time.end;
    }
}

function groupByParameterId(segmentMap: Map<string, IntermediateSegment>): Map<number, PublicBracketScheduleInfo> {
    const map = new Map<number, PublicBracketScheduleInfo>();
    for (const seg of segmentMap.values()) {
        const segInfo: PublicBracketSegmentInfo = {
            matNames: seg.matNames,
            etaStart: seg.etaStart,
            etaEnd: seg.etaEnd,
            roundMarkerFrom: seg.roundMarkerFrom,
            roundMarkerTo: seg.roundMarkerTo,
        };
        const existing = map.get(seg.parameterId);
        if (existing) {
            existing.segments.push(segInfo);
            mergeMatNames(existing.allMatNames, seg.matNames);
        } else {
            map.set(seg.parameterId, { segments: [segInfo], allMatNames: [...seg.matNames] });
        }
    }
    for (const info of map.values()) {
        info.segments.sort((a, b) => {
            if (!a.etaStart) {
                return -1;
            }
            if (!b.etaStart) {
                return 1;
            }
            return a.etaStart.localeCompare(b.etaStart);
        });
    }
    return map;
}

function mergeMatNames(target: string[], source: string[]): void {
    for (const name of source) {
        if (!target.includes(name)) {
            target.push(name);
        }
    }
}

export function hasMultiplePublicBracketScheduleDays(
    scheduleInfoMap: Map<number, PublicBracketScheduleInfo>,
    eventTimezone: string,
): boolean {
    const dates = new Set<string>();
    for (const info of scheduleInfoMap.values()) {
        for (const seg of info.segments) {
            if (seg.etaStart) {
                dates.add(convertToTimezone(seg.etaStart, eventTimezone, "yyyy-MM-dd"));
            }
        }
    }
    return dates.size > 1;
}

export function formatSegmentEta(segment: PublicBracketSegmentInfo, eventTimezone: string, hasMultipleDays: boolean): string {
    if (!segment.etaStart || !segment.etaEnd) {
        return "";
    }

    const start = convertToTimezone(segment.etaStart, eventTimezone, "HH:mm");
    const end = convertToTimezone(segment.etaEnd, eventTimezone, "HH:mm");

    if (hasMultipleDays) {
        const day = convertToTimezoneLocalized(segment.etaStart, eventTimezone, "EEE");
        return `${day} ${start} – ${end}`;
    }

    return `${start} – ${end}`;
}
